<template>
  <div class="container-fluid p-5">
    <loading :active.sync="isLoading" :is-full-page="fullPage" />

    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <!-- Header -->
          <div class="card-header">
            User Activity Report
          </div>

          <!-- Filters -->
          <div class="card-body border-bottom">
            <div class="row">
              <div class="col-md-3">
                <label>Name</label>
                <input
                  v-model="filter.name"
                  type="text"
                  class="form-control"
                  placeholder="User name"
                />
              </div>

              <div class="col-md-3">
                <label>Phone / Email</label>
                <input
                  v-model="filter.phoneOrEmail"
                  type="text"
                  class="form-control"
                  placeholder="Phone or Email"
                />
              </div>

              <div class="col-md-2">
                <label>Platform</label>
                <select v-model="filter.platform" class="form-control">
                  <option value="">All</option>
                  <option value="web">Web</option>
                  <option value="android">Android</option>
                  <option value="ios">iOS</option>
                </select>
              </div>

              <div class="col-md-2">
                <label>From</label>
                <input v-model="filter.from" type="date" class="form-control" />
              </div>

              <div class="col-md-2">
                <label>To</label>
                <input v-model="filter.to" type="date" class="form-control" />
              </div>
            </div>

            <div class="mt-3">
              <button class="btn btn-primary mr-2" @click="search">
                <i class="fas fa-search"></i> Search
              </button>
              <button class="btn btn-secondary" @click="reset">
                Reset
              </button>
            </div>
          </div>

          <!-- Table -->
          <div class="card-body">
            <div class="table-responsive">
              <table class="common-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Phone / Email</th>
                    <th>Device</th>
                    <th>Platform</th>
                    <th>Date</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="item in dataList" :key="item.id">
                    <td>{{ item.id }}</td>
                    <td>{{ item.user_name }}</td>
                    <td>{{ item.phone_or_email }}</td>
                    <td>{{ item.device_name }}</td>
                    <td class="text-capitalize">{{ item.platform }}</td>
                    <td>{{ item.date }}</td>
                  </tr>

                  <tr v-if="dataList.length === 0">
                    <td colspan="6" class="text-center">
                      Empty List
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <div class="table-pagination-minimal">
                <div class="table-pagination-row">
                  <div v-if="dataList.length" class="table-info-minimal">
                    Showing {{ metaData.from }} to {{ metaData.to }} of
                    {{ metaData.total }} entries
                  </div>

                  <nav class="pagination-minimal">
                    <ul>
                      <li :class="{ disabled: !links.prev }">
                        <button
                          @click.prevent="next(metaData.current_page - 1)"
                          :disabled="!links.prev"
                        >
                          <i class="fas fa-angle-left"></i>
                        </button>
                      </li>

                      <li
                        v-for="page in visiblePages"
                        :key="page"
                        :class="{ active: metaData.current_page === page }"
                      >
                        <button @click.prevent="next(page)">
                          {{ page }}
                        </button>
                      </li>

                      <li :class="{ disabled: !links.next }">
                        <button
                          @click.prevent="next(metaData.current_page + 1)"
                          :disabled="!links.next"
                        >
                          <i class="fas fa-angle-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  name: "UserActivityList",
  metaInfo: {
    title: "User Activity",
    titleTemplate: "%s ← BXB",
  },
  components: { Loading },

  data() {
    return {
      isLoading: false,
      fullPage: true,

      dataList: [],

      filter: {
        perPage: 20,
        page: 1,
        name: "",
        phoneOrEmail: "",
        platform: "",
        from: "",
        to: "",
      },

      metaData: {
        current_page: 1,
        last_page: null,
        from: null,
        to: null,
        total: null,
      },

      links: {
        prev: null,
        next: null,
      },
    };
  },

  computed: {
    visiblePages() {
      const last = this.metaData.last_page;
      const current = this.metaData.current_page;
      if (!last) return [];

      const pages = [];
      for (let i = 1; i <= last; i++) {
        if (Math.abs(i - current) < 3 || i === 1 || i === last) {
          pages.push(i);
        }
      }
      return pages;
    },
  },

  methods: {
    ...mapActions({
      getUserActivityAction: "getUserActivityAction",
    }),

    async fetchData() {
      this.isLoading = true;
      try {
        const payload = {
          perPage: this.filter.perPage,
          page: this.filter.page,
          name: this.filter.name,
          phoneOrEmail: this.filter.phoneOrEmail,
          platform: this.filter.platform,
          from: this.filter.from,
          to: this.filter.to,
        };

        const res = await this.getUserActivityAction(payload);

        if (res.success) {
          this.dataList = res.data.data;

          const meta = res.data.meta;
          this.metaData = {
            current_page: meta.currentPage,
            last_page: meta.lastPage,
            from: meta.from,
            to: meta.to,
            total: meta.total,
          };

          this.links = {
            prev: res.data.links.prev,
            next: res.data.links.next,
          };
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    search() {
      this.filter.page = 1;
      this.fetchData();
    },

    reset() {
      this.filter = {
        perPage: 20,
        page: 1,
        name: "",
        phoneOrEmail: "",
        platform: "",
        from: "",
        to: "",
      };
      this.fetchData();
    },

    next(page) {
      if (page < 1 || page > this.metaData.last_page) return;
      this.filter.page = page;
      this.fetchData();
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>
